import { withHydrate } from '@relay/react/hooks';
import ApiService from '@core/api/services/ApiService';
import { isListingCardModel } from '@core/typeguards';
import UrlResolver from '@core/url-resolver/UrlResolver';
import { CaseInsensitiveNumberParam, useCaseInsensitiveQueryParam } from '@helpers/caseInsensitiveParam';
import { Device, from, until } from '@helpers/media';
import { isSSR } from '@helpers/ssr';
import withRedux from '@helpers/withRedux';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import { ProductCardsProvider } from '@hooks/useProductCards';
import { addRemoveProduct, toggle } from '@state/ducks/compare/compare.reducer';
import InfoModalNPI from '@stories/Components/Content/InfoModalNPI/InfoModalNPI';
import ProductCard from '@stories/Elements/Cards/ProductCard/ProductCard';
import PromoCard from '@stories/Elements/Cards/PromoCard/PromoCard';
import Loader from '@stories/Elements/Misc/Loader/Loader.hybrid';
import { Column } from '@stories/Settings/Grids/Grid.styles';
import React, { useEffect, useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { useDispatch, useSelector } from 'react-redux';
import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from 'use-query-params';
import ProductListingFilters, { CommaArrayParam } from '../ProductListingFilters/ProductListingFilters';
import { ProductListStyles } from './ProductList.styles';
import ProductListItemsGrouped from './ProductListItemsGrouped';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export let ListingType = /*#__PURE__*/function (ListingType) {
  ListingType[ListingType["DEFAULT"] = 0] = "DEFAULT";
  ListingType[ListingType["PROMOTION_LISTING"] = 1] = "PROMOTION_LISTING";
  return ListingType;
}({});
const ProductList = props => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [ref, inView] = useInView({
    threshold: 0.15
  }, true);
  const [showAgilityId] = useQueryParam('showagilityid', withDefault(StringParam, 'false'));
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [allFilters, setAllFilters] = useState([]);
  const [showInfoModal, setShowInfoModal] = useState(null);
  const singleCardOnMobile = props.listingType === ListingType.PROMOTION_LISTING;
  const rowItems = useMedia([from(Device.TabletLarge), from(Device.Mobile)], [allFilters.length == 0 ? 4 : 3, singleCardOnMobile ? 1 : 2], 1);
  const [urlPageIndex, setUrlPageIndex] = useCaseInsensitiveQueryParam('pageIndex', CaseInsensitiveNumberParam, 0);
  const [urlPageSize, setUrlPageSize] = useCaseInsensitiveQueryParam('pageSize', CaseInsensitiveNumberParam, rowItems * 10);
  const [pageIndex, setPageIndex] = React.useState(urlPageIndex ?? 0);
  const pageSize = urlPageSize ?? rowItems * 10;
  const initialUrlFilters = React.useMemo(() => {
    if (!isSSR) {
      const searchParams = new URLSearchParams(window.location.search);
      const values = {};
      searchParams.forEach((v, k) => {
        const split = v.split(',');
        values[k] = split.every(x => x === '') ? [] : split;
      });
      return values;
    }
    return {};
  }, []);
  const [trades] = useQueryParam('trades', withDefault(NumberParam, 0));
  // eslint-disable-next-line
  const [value, setValue] = useState({});
  const pageRange = useMedia([until(Device.Tablet)], [2], 5);

  // Compare
  const dispatch = useDispatch();
  const {
    showCompare,
    items
  } = useSelector(x => x.compare);
  React.useEffect(() => {
    if (props.enableCompare && !showCompare) {
      dispatch(toggle());
    }
  }, [props.enableCompare, showCompare]);
  const updateCompare = (agilityId, image, productName, modelCode) => {
    const isInCompare = items.some(item => item.agilityId === agilityId);
    if (items.length < 4 || isInCompare) {
      dispatch(addRemoveProduct({
        agilityId: agilityId,
        image: image,
        productName: productName,
        modelCode: modelCode
      }));
    }
  };
  const pageCount = useMemo(() => {
    if (!response) {
      return 0;
    }
    return Math.ceil(response.totalCount / pageSize);
  }, [response, response?.totalCount]);
  const urlFilters = response?.filters.filter(x => x.key !== 'childCategory') || null;
  const queryParamsConfig = React.useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const config = {};
    if (urlFilters === null) {
      config['orderby'] = withDefault(CommaArrayParam, [props.sortNewestByDefault ? 'newest' : 'relevance']);
      return config;
    }
    const filterKeys = urlFilters.map(x => x.key);
    if (props.groupByCategory) {
      filterKeys.push('categoryPageNumber');
    }
    for (const key of filterKeys) {
      if (key === 'orderby') {
        config[key] = withDefault(CommaArrayParam, [props.sortNewestByDefault ? 'newest' : 'relevance']);
      } else {
        config[key] = withDefault(CommaArrayParam, []);
      }
    }
    return config;
  }, [urlFilters]);
  const [queryParamsValue, setQueryParamsValue] = useQueryParams(queryParamsConfig);
  const _setValue = _value => {
    setQueryParamsValue(_value);
    setValue(_value); // Update external state too to fetch data //
  };
  const onFilterChange = async (key, selectedValues, replace = false) => {
    const collectionToUse = queryParamsValue;
    const existingFilter = collectionToUse[key];

    // Reset the page index when filters change
    setPageIndex(0);
    if (replace === true) {
      const newFilters = {
        ...collectionToUse,
        [key]: [...selectedValues]
      };
      _setValue(newFilters);
      return;
    }
    if (selectedValues.length === 0) {
      const newFilters = {
        ...collectionToUse,
        [key]: []
      };
      _setValue(newFilters);
      return;
    }
    if (selectedValues.length > 1) {
      const newFilters = {
        ...collectionToUse,
        [key]: selectedValues
      };
      _setValue(newFilters);
      return;
    }
    const selectedValue = selectedValues[0];
    const existingValues = [...existingFilter];
    const existingIndex = existingValues.indexOf(selectedValue);
    if (existingIndex === -1) {
      existingValues.push(selectedValue);
    } else {
      existingValues.splice(existingIndex, 1);
    }
    const newFilters = {
      ...collectionToUse,
      [key]: existingValues
    };
    _setValue(newFilters);
  };
  const getListingCards = () => {
    let cards = response?.cards;
    if ((cards === undefined || cards?.length === 0) && props.promoCards !== null && props.promoCards?.length > 0) {
      cards = props.promoCards;
    }
    return cards ?? [];
  };
  const allCards = useMemo(() => {
    if (!response || !response.products) {
      return [];
    }
    const listingCards = getListingCards();
    const allCards = [];
    for (let index = 0; index < pageSize; index++) {
      const product = response.products[index];
      const promoCard = listingCards.find(x => x.position - 1 === index);
      if (promoCard) {
        allCards.push(promoCard);
      }
      if (product) {
        allCards.push(product);
      }
    }
    return allCards;
  }, [response, response?.products, props.promoCards]);
  useEffect(() => {
    async function fetchProducts() {
      if (!isFirstLoad && ref.current && !inView) {
        // Wait a small amount of time to allow the scroll to finish //
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        await new Promise(r => setTimeout(r, 750));
      }
      const params = {
        allFilters: !response ? {
          ...initialUrlFilters
        } : {
          ...value
        },
        culture: props.pageContext.documentCulture,
        filterWhitelist: props.filterWhitelist,
        groupByCategory: props.groupByCategory,
        includeAllCategories: props.includeAllCategories,
        pageIndex: pageIndex,
        pageSize: pageSize,
        productListGuid: props.productListGuid,
        productListGuids: props.filterWhitelist.indexOf('productlists') > -1 && props.productListGuids ? props.productListGuids : null,
        promoCards: null,
        published: props.published ?? true,
        showCards: props.showCards,
        includePreviousPages: false,
        searchTerm: props.searchTerm ? props.searchTerm : null,
        showAgilityId: showAgilityId,
        tradeAgilityId: props.tradeAgilityId,
        url: props.includeAllProducts ? null : props.url ? props.url : window.location.pathname,
        productViewModelOptions: props.productViewModelOptions,
        excludeComingSoon: props.excludeComingSoon,
        onlyNew: props.onlyNew,
        includeAllFilters: isFirstLoad
      };
      setIsLoading(true);
      const _response = await ApiService.request({
        baseUrl: UrlResolver.getDomain(),
        controller: 'productlisting',
        slug: props.listingType === ListingType.PROMOTION_LISTING ? 'web-promotions' : 'listingproductmodels',
        method: 'POST',
        params: {
          ...params,
          allFilters: {
            ...params.allFilters,
            orderby: params.allFilters.orderby?.length > 0 ? params.allFilters.orderby : [props.sortNewestByDefault ? 'newest' : 'relevance']
          }
        }
      });
      if (props.setTotalCount) {
        let totalCount = 0;
        for (let index = 0; index < _response.groupedProducts.length; index++) {
          const group = _response.groupedProducts[index];
          totalCount += group.productCount;
        }
        props.setTotalCount(totalCount);
      }

      // TODO - Remove following systems implementation //
      if (process.env.NODE_ENV === 'development') {
        if (_response.allFilters) {
          _response.allFilters[0].tooltip = {
            title: `Understanding Our <span>Trade</span> Filter`,
            content: `Explore tools and equipment tailored for your profession. Whether it's construction, plumbing, woodworking, or electrical tasks, find products crafted for your specific trade.<br/><strong>You can select multiple trades if your work encompasses various specialties.</strong>`
          };
        }
        _response.filters[0].tooltip = {
          title: `Understanding Our <span>Trade</span> Filter`,
          content: `Explore tools and equipment tailored for your profession. Whether it's construction, plumbing, woodworking, or electrical tasks, find products crafted for your specific trade.<br/><strong>You can select multiple trades if your work encompasses various specialties.</strong>`
        };
      }
      if (allFilters.length === 0 && _response.allFilters && _response.allFilters.length > 0) {
        // Remove "orderby" and "childCategory" filters from the list, as well as empty filters as these are not displayed in ProductListingFilters - keeps data-hide-filters accurate //
        setAllFilters(_response.allFilters.filter(x => x.key !== 'orderby' && x.key !== 'childCategory' && x.filterOptions.length > 0));
        if (props.setChildCategories) {
          const childCategory = _response.allFilters.find(x => x.key === 'childCategory');
          if (childCategory) {
            props.setChildCategories(childCategory);
          }
        }
      }
      setResponse(_response);
      setIsLoading(false);
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }
    }
    fetchProducts();
  }, [value, pageIndex, pageSize]);
  useEffect(() => {
    async function fetchTradeBanner() {
      if (props.setTradeBanner === undefined) {
        return;
      }
      const response = await ApiService.request({
        baseUrl: UrlResolver.getDomain(),
        controller: 'productlisting',
        slug: 'tradebanner',
        method: 'GET',
        params: {
          tradeAgilityId: trades,
          cultureCode: props.pageContext.documentCulture
        }
      });
      props.setTradeBanner(response.banner);
    }
    if (trades > 0) {
      fetchTradeBanner();
    }
  }, [trades]);
  const renderCard = (card, index) => {
    if (isListingCardModel(card)) {
      return /*#__PURE__*/_jsx(PromoCard, {
        viewModel: card
      }, card.title);
    } else {
      return /*#__PURE__*/_jsx(ProductCard, {
        product: card,
        buttonText: props.listingType === ListingType.PROMOTION_LISTING ? props.translations['custom.milwaukee.promotion.view'] || '' : props.translations['custom.products.view'] || '',
        index: index,
        pageContext: props.pageContext,
        showPrice: false,
        translations: props.translations,
        icons: response?.icons,
        showSystemLogos: response?.showSystemLogos,
        showCompare: showCompare,
        updateCompare: updateCompare,
        isInCompare: items.some(item => item.agilityId === card.agilityId),
        compareItems: items,
        rowItems: rowItems,
        productInfoModal: props.productInfoModal,
        setShowInfoModal: setShowInfoModal,
        hidePublishedTag: props.hidePublishedTag
      }, card.agilityId);
    }
  };
  useEffect(() => {
    if (!isFirstLoad) {
      setUrlPageIndex(pageIndex);
      setUrlPageSize(pageSize);
    }
  }, [pageIndex]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showInfoModal != null ? /*#__PURE__*/_jsx(React.Suspense, {
      fallback: null,
      children: /*#__PURE__*/_jsx(InfoModalNPI, {
        isOpen: true,
        onClose: () => setShowInfoModal(null),
        product: showInfoModal,
        translations: props.translations
      })
    }) : null, /*#__PURE__*/_jsxs(ProductListStyles.Wrapper, {
      "data-hide-filters": allFilters.length == 0,
      activeZIndex: props.filtersOpenByDefault || false,
      children: [/*#__PURE__*/_jsx(ProductListingFilters, {
        isLoading: isLoading,
        translations: props.translations,
        response: response,
        urlFilters: urlFilters
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ,
        setFilterValue: filters => {
          setValue(filters);
        },
        groupByCategory: props.groupByCategory,
        sortNewestByDefault: props.sortNewestByDefault,
        filters: allFilters,
        value: queryParamsValue,
        setValue: setQueryParamsValue,
        expandAllFilters: props.expandAllFilters
      }), /*#__PURE__*/_jsx(ProductListStyles.ProductListWrapper, {
        children: /*#__PURE__*/_jsxs(Column.Full, {
          children: [/*#__PURE__*/_jsx("div", {
            ref: ref
          }), isLoading ? /*#__PURE__*/_jsx(ProductListStyles.LoadingWrapper, {
            children: /*#__PURE__*/_jsx(Loader, {})
          }) : /*#__PURE__*/_jsx(_Fragment, {
            children: props.groupByCategory && response?.groupedProducts ? /*#__PURE__*/_jsx(ProductListItemsGrouped, {
              translations: props.translations,
              groupedProducts: response.groupedProducts,
              onLoadMore: (key, value) => onFilterChange(key, value, true),
              value: queryParamsValue['categoryPageNumber'],
              pageSize: pageSize
            }) : /*#__PURE__*/_jsx(ProductListStyles.ProductList, {
              singleCardOnMobile: props.listingType === ListingType.PROMOTION_LISTING,
              children: /*#__PURE__*/_jsx(ProductCardsProvider, {
                pageSize: pageSize,
                products: response?.products ?? [],
                rowItems: rowItems,
                iconMapping: response?.icons ?? [],
                cards: getListingCards() ?? [],
                cultureCode: props.pageContext.documentCulture,
                children: allCards.map((x, index) => renderCard(x, index))
              })
            })
          }), response?.canLoadMore ? /*#__PURE__*/_jsx(ProductListStyles.ButtonContainer, {
            children: /*#__PURE__*/_jsx(ProductListStyles.Pagination, {
              children: /*#__PURE__*/_jsx(ReactPaginate, {
                marginPagesDisplayed: 1,
                pageRangeDisplayed: pageRange,
                pageCount: pageCount,
                initialPage: pageIndex,
                forcePage: pageIndex,
                onPageChange: e => setPageIndex(e.selected),
                nextLabel: null,
                previousLabel: null
              })
            })
          }) : null]
        })
      })]
    })]
  });
};
export const clientConfig = {
  hydrate: 'always'
};
export default withHydrate(withRedux(ProductList), 'ProductList', 'hybrid');
